import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import IconButton from '@material-ui/core/IconButton'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import AttachMoneyIcon from 'components/material-symbol/icons/AttachMoney'
import BarChartIcon from 'components/material-symbol/icons/BarChart'
import CloseIcon from 'components/material-symbol/icons/Close'
import EmojiEventsIcon from 'components/material-symbol/icons/EmojiEvents'
import PercentIcon from 'components/material-symbol/icons/Percent'
import PhotoCameraIcon from 'components/material-symbol/icons/PhotoCamera'
import PublicIcon from 'components/material-symbol/icons/Public'
import { CARD_HEADER_BORDER } from 'lib/styles/card-header'
import {
  DASHBOARD_BODY_TEXT,
  DASHBOARD_SURFACE_MUTED,
} from 'pages/pga-coach/dashboard/dashboard-colors'
import {
  TOP_COACHES_DETAILS,
  TOP_COACHES_DIALOG_SUBTITLE,
  TOP_COACHES_DIALOG_TITLE,
  getTopCoachesDefinition,
  getTopCoachesDefinitionItem,
  getTopCoachesStats,
} from './constants'

const TITLE_ID = 'top-coaches-definition-title'

const TOP_COACHES_ICONS = {
  'top-coaches': PercentIcon,
  'lesson-revenue': AttachMoneyIcon,
  nationwide: PublicIcon,
  medians: BarChartIcon,
  snapshot: PhotoCameraIcon,
}

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
      width: `calc(100% - ${theme.spacing(4)}px)`,
      maxHeight: `calc(100% - ${theme.spacing(4)}px)`,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '0 0 auto',
    padding: theme.spacing(2.5, 7, 2.5, 3),
    borderBottom: `1px solid ${CARD_HEADER_BORDER}`,
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 6, 2, 2),
    },
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: DASHBOARD_SURFACE_MUTED,
    color: DASHBOARD_BODY_TEXT,
    '& .MuiSvgIcon-root': {
      fontSize: 22,
    },
  },
  headerText: {
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: DASHBOARD_BODY_TEXT,
  },
  subtitle: {
    marginTop: theme.spacing(0.25),
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('xs')]: {
      top: theme.spacing(1),
      right: theme.spacing(1),
    },
  },
  content: {
    padding: theme.spacing(3, 3, 1),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 0.5),
    },
  },
  stats: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  stat: {
    flex: '1 1 0',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: '#F4F7F9',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.25),
    },
  },
  statValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#00205B',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9375rem',
      whiteSpace: 'nowrap',
    },
  },
  statLabel: {
    marginTop: theme.spacing(0.25),
    fontSize: '0.75rem',
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${CARD_HEADER_BORDER}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  itemIcon: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: DASHBOARD_SURFACE_MUTED,
    color: DASHBOARD_BODY_TEXT,
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },
  itemText: {
    minWidth: 0,
  },
  itemLabel: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.4,
    color: DASHBOARD_BODY_TEXT,
  },
  itemDescription: {
    marginTop: theme.spacing(0.25),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  actions: {
    flex: '0 0 auto',
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${CARD_HEADER_BORDER}`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
}))

const DefinitionItem = ({ item, classes }) => {
  const Icon = TOP_COACHES_ICONS[item.id]

  return (
    <li className={classes.item}>
      <span className={classes.itemIcon} aria-hidden="true">
        {Icon ? <Icon /> : null}
      </span>
      <div className={classes.itemText}>
        <Typography component="h3" className={classes.itemLabel}>
          {item.label}
        </Typography>
        <Typography className={classes.itemDescription}>
          {item.description}
        </Typography>
      </div>
    </li>
  )
}

DefinitionItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
}

export const TopCoachesLink = ({ children, cohortSize }) => {
  const [open, setOpen] = useState(false)
  const classes = useStyles()
  const close = () => setOpen(false)
  const items = [
    getTopCoachesDefinitionItem(cohortSize),
    ...TOP_COACHES_DETAILS,
  ]

  return (
    <>
      <Link
        component="button"
        type="button"
        variant="inherit"
        color="inherit"
        underline="always"
        style={{
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          textAlign: 'inherit',
          verticalAlign: 'baseline',
        }}
        onClick={() => setOpen(true)}
      >
        {children}
      </Link>
      <Dialog
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        aria-labelledby={TITLE_ID}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.header}>
          <span className={classes.headerIcon} aria-hidden="true">
            <EmojiEventsIcon />
          </span>
          <div className={classes.headerText}>
            <Typography component="h2" id={TITLE_ID} className={classes.title}>
              {TOP_COACHES_DIALOG_TITLE}
            </Typography>
            <Typography className={classes.subtitle}>
              {TOP_COACHES_DIALOG_SUBTITLE}
            </Typography>
          </div>
          <IconButton
            aria-label="Close dialog"
            className={classes.closeButton}
            onClick={close}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent className={classes.content}>
          <div className={classes.stats}>
            {getTopCoachesStats(cohortSize).map(stat => (
              <div key={stat.id} className={classes.stat}>
                <Typography className={classes.statValue}>
                  {stat.value}
                </Typography>
                <Typography className={classes.statLabel}>
                  {stat.label}
                </Typography>
              </div>
            ))}
          </div>
          <ul className={classes.list}>
            {items.map(item => (
              <DefinitionItem key={item.id} item={item} classes={classes} />
            ))}
          </ul>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disableElevation
            startIcon={<CloseIcon fontSize="small" />}
            onClick={close}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

TopCoachesLink.propTypes = {
  children: PropTypes.node.isRequired,
  cohortSize: PropTypes.number,
}

export const TopCoachesSummary = ({ cohortSize, className, children }) => (
  <Typography className={className}>
    <TopCoachesLink cohortSize={cohortSize}>Top coaches</TopCoachesLink>{' '}
    {getTopCoachesDefinition(cohortSize)}
    {children ? <> {children}</> : null}
  </Typography>
)

TopCoachesSummary.propTypes = {
  cohortSize: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
}
